// TS port of apk/lib/services/compound_linkifier.dart — render compound-mentioning
// prose with tap-to-navigate links pointing at other compounds in the catalog.
//
// Algorithm (mirrors the Dart impl):
//   - Build index: tokens = each compound's display_name + qualified aliases
//   - Whole-word, case-insensitive, longest-match-wins
//   - Aliases >= 4 chars OR containing a digit (B12, P-5-P, D3) qualify; others skipped
//   - Hardcoded stoplist drops generic English aliases ("water", "tea", "salt", ...)
//   - Don't link a compound to itself
//   - Only the FIRST mention of each target per text block becomes a link
//   - All non-matched text is HTML-escaped; matched spans become <a class="xlink"> tags

import type { Compound } from './compounds';

const STOP_ALIASES = new Set([
  'water', 'sugar', 'salt', 'tea', 'oil', 'fat', 'food', 'alcohol',
  'meal', 'meat', 'fish', 'milk', 'iron', 'lead', 'gold', 'silver',
]);

type Index = {
  re: RegExp;
  lowerToId: Map<string, string>;
};

let CACHE: Index | null = null;
let CACHE_KEY = -1;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildIndex(byId: Record<string, Compound>): Index {
  const key = Object.keys(byId).length;
  if (CACHE && CACHE_KEY === key) return CACHE;
  const tokens: { lower: string; id: string }[] = [];
  for (const c of Object.values(byId)) {
    const dn = (c.display_name || '').trim();
    if (dn) tokens.push({ lower: dn.toLowerCase(), id: c.id });
    for (const a of c.aliases || []) {
      const t = (a || '').trim();
      const hasDigit = /\d/.test(t);
      if (t.length < 4 && !hasDigit) continue;
      if (t.length < 2) continue;
      if (STOP_ALIASES.has(t.toLowerCase())) continue;
      tokens.push({ lower: t.toLowerCase(), id: c.id });
    }
  }
  tokens.sort((a, b) => b.lower.length - a.lower.length);
  const lowerToId = new Map<string, string>();
  for (const t of tokens) {
    if (!lowerToId.has(t.lower)) lowerToId.set(t.lower, t.id);
  }
  const pattern = '\\b(' + Array.from(lowerToId.keys()).map(escapeRegex).join('|') + ')\\b';
  const re = new RegExp(pattern, 'gi');
  CACHE = { re, lowerToId };
  CACHE_KEY = key;
  return CACHE;
}

/**
 * Render `text` as HTML with cross-compound links inserted at the first mention
 * of each other compound. Non-matched text is HTML-escaped. Pass the current
 * compound's id as `exceptId` so it doesn't self-link.
 */
export function linkifyHtml(text: string, byId: Record<string, Compound>, exceptId?: string): string {
  if (!text) return '';
  const idx = buildIndex(byId);
  if (idx.lowerToId.size === 0) return escapeHtml(text);
  const seen = new Set<string>();
  let out = '';
  let cursor = 0;
  // Reset lastIndex because the regex has the global flag.
  idx.re.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = idx.re.exec(text)) !== null) {
    const matched = m[0];
    const targetId = idx.lowerToId.get(matched.toLowerCase());
    if (!targetId || targetId === exceptId || seen.has(targetId)) {
      continue;
    }
    if (m.index > cursor) {
      out += escapeHtml(text.substring(cursor, m.index));
    }
    out += `<a class="xlink" href="/c/${encodeURIComponent(targetId)}">${escapeHtml(matched)}</a>`;
    seen.add(targetId);
    cursor = m.index + matched.length;
  }
  if (cursor < text.length) {
    out += escapeHtml(text.substring(cursor));
  }
  return out;
}
